/**
 * JWT session persistence.
 *
 * The token itself never leaves the `Authorization` header — never in a URL or
 * query string. We also stash the user record the backend returns at login so
 * the console can render the current user without a follow-up `/auth/me` call
 * (the backend already includes it in `TokenResponse`).
 *
 * When the reviewer opts into Remember-me the session lives in `localStorage`
 * (30 days, survives browser restart). When they don't, it lives in
 * `sessionStorage` (tab-lifetime — closing the tab signs them out). The
 * `rememberMe` flag on the stored record picks which one on read/write.
 *
 * All mutations broadcast `pyxis:auth-changed` on the window so React hooks in
 * this tab can react synchronously; the browser fires `storage` for *other*
 * tabs, and both paths call the same subscriber.
 */

const APP_ENV = (import.meta.env?.["VITE_APP_ENV"] as string | undefined) ?? "dev"
const STORAGE_KEY = `pyxis:auth:${APP_ENV}`

const AUTH_CHANGED_EVENT = "pyxis:auth-changed"

export interface StoredUser {
  id: string
  email: string
  name: string | null
  role: string
}

export interface AuthSession {
  token: string
  user: StoredUser
  /** Epoch millis for the access token. Used only to preempt an
   *  obviously-dead token — the server is the authorization boundary. */
  expiresAt: number
  /** Long-lived refresh token when the reviewer ticked Remember-me at
   *  sign-in; null otherwise. Rotates on every /auth/refresh call. */
  refreshToken: string | null
  /** Epoch millis. When the refresh token also expires, the session is
   *  fully dead — nothing left to rotate. */
  refreshExpiresAt: number | null
  /** Picks the backing store: true → localStorage (survives restart),
   *  false → sessionStorage (dies with the tab). */
  rememberMe: boolean
}

// useSyncExternalStore compares snapshots by Object.is, so returning a
// freshly-parsed object every read triggers an infinite render loop. Cache
// the parsed value keyed by the raw string and only re-parse when it changes.
let cachedRaw: string | null = null
let cachedSession: AuthSession | null = null

function readRaw(): string | null {
  try {
    return (
      globalThis.localStorage?.getItem(STORAGE_KEY) ??
      globalThis.sessionStorage?.getItem(STORAGE_KEY) ??
      null
    )
  } catch {
    return null
  }
}

function safeGet(): AuthSession | null {
  try {
    const raw = readRaw()
    if (raw === cachedRaw) return cachedSession
    cachedRaw = raw
    if (!raw) {
      cachedSession = null
      return null
    }
    const parsed = JSON.parse(raw) as unknown
    cachedSession = isSession(parsed) ? parsed : null
    return cachedSession
  } catch {
    cachedRaw = null
    cachedSession = null
    return null
  }
}

function isSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== "object") return false
  // Bracket access on the index-signature type is required by strict mode
  // (`noPropertyAccessFromIndexSignature: true`) — dot access is forbidden
  // because the compiler cannot prove any particular key exists on an
  // arbitrary `Record<string, unknown>`.
  const s = value as Record<string, unknown>
  if (typeof s["token"] !== "string" || typeof s["expiresAt"] !== "number") return false
  if (typeof s["rememberMe"] !== "boolean") return false
  if (s["refreshToken"] !== null && typeof s["refreshToken"] !== "string") return false
  if (s["refreshExpiresAt"] !== null && typeof s["refreshExpiresAt"] !== "number") return false
  const u = s["user"] as Record<string, unknown> | undefined
  if (
    !u ||
    typeof u["id"] !== "string" ||
    typeof u["email"] !== "string" ||
    typeof u["role"] !== "string"
  ) {
    return false
  }
  return true
}

function broadcast(): void {
  try {
    globalThis.dispatchEvent?.(new Event(AUTH_CHANGED_EVENT))
  } catch {
    /* SSR or blocked event constructor — subscribers still catch cross-tab
       via the storage event. */
  }
}

export function readAuthSession(): AuthSession | null {
  return safeGet()
}

export function writeAuthSession(session: AuthSession): void {
  const payload = JSON.stringify(session)
  try {
    // Ensure the key doesn't linger in the other store — a switch from
    // Remember-me on → off must not leave a copy in localStorage.
    if (session.rememberMe) {
      globalThis.sessionStorage?.removeItem(STORAGE_KEY)
      globalThis.localStorage?.setItem(STORAGE_KEY, payload)
    } else {
      globalThis.localStorage?.removeItem(STORAGE_KEY)
      globalThis.sessionStorage?.setItem(STORAGE_KEY, payload)
    }
    broadcast()
  } catch {
    /* Storage unavailable (private mode, SSR). Session lives in memory only. */
  }
}

export function clearAuthSession(): void {
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY)
    globalThis.sessionStorage?.removeItem(STORAGE_KEY)
    broadcast()
  } catch {
    /* Storage unavailable. */
  }
}

/**
 * Subscribe to session changes in *any* tab. Returns an unsubscribe fn.
 * `storage` fires only for OTHER tabs, so we listen to our own custom event
 * too — same subscriber, both sources.
 */
export function subscribeAuthSession(fn: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) fn()
  }
  globalThis.addEventListener?.("storage", onStorage)
  globalThis.addEventListener?.(AUTH_CHANGED_EVENT, fn)
  return () => {
    globalThis.removeEventListener?.("storage", onStorage)
    globalThis.removeEventListener?.(AUTH_CHANGED_EVENT, fn)
  }
}

// ── Legacy token-only helpers (http.ts and tests read/write just the token). ──

export function readAuthToken(): string | null {
  return safeGet()?.token ?? null
}

export function writeAuthToken(token: string): void {
  const existing = safeGet()
  if (existing) {
    writeAuthSession({ ...existing, token })
    return
  }
  writeAuthSession({
    token,
    user: { id: "", email: "", name: null, role: "" },
    expiresAt: 0,
    refreshToken: null,
    refreshExpiresAt: null,
    rememberMe: false,
  })
}

export function clearAuthToken(): void {
  clearAuthSession()
}

/**
 * Swap the access token on the current session (used by the refresh
 * loop in http.ts). Returns the updated session, or null if there was
 * no session to update.
 */
export function updateAccessToken(
  token: string,
  expiresAt: number,
  refreshToken: string | null,
  refreshExpiresAt: number | null,
): AuthSession | null {
  const existing = safeGet()
  if (!existing) return null
  const next: AuthSession = {
    ...existing,
    token,
    expiresAt,
    refreshToken: refreshToken ?? existing.refreshToken,
    refreshExpiresAt: refreshExpiresAt ?? existing.refreshExpiresAt,
  }
  writeAuthSession(next)
  return next
}
